import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Sidebar from '../../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../../components/Dashboard/Header/Header';
import Breadcrumbs from '../../../components/Common/Breadcrumbs/Breadcrumbs';
import CustomSelect from '../../../components/Common/Select/CustomSelect';
import CustomDatePicker from '../../../components/Common/DatePicker/CustomDatePicker';
import api from '../../../api/axios';
import {
  Info,
  FileText,
  Upload,
  Plus,
  Trash2,
  X,
  IndianRupee,
  Loader2,
  Calendar,
  Link as LinkIcon,
  List,
  Check
} from 'lucide-react';
import styles from './CreateInvoice.module.css';
import toast from 'react-hot-toast';
import NotFound from '../../Error/NotFound';

const EditInvoice = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  // Dropdown data
  const [projectsData, setProjectsData] = useState([]);
  const [customersData, setCustomersData] = useState([]);
  const [taxesData, setTaxesData] = useState([]);
  const [workOrdersData, setWorkOrdersData] = useState([]);
  const [fullItemsData, setFullItemsData] = useState([]);

  const [fetchingWorkOrders, setFetchingWorkOrders] = useState(false);
  const [selectedWorkOrders, setSelectedWorkOrders] = useState([]);
  const [orderItems, setOrderItems] = useState([]);

  const [formData, setFormData] = useState({
    invoice_number: '',
    invoice_date: '',
    due_date: '',
    customer_id: '',
    project_id: '',
    currency: 'INR',
    notes: '',
    terms: '',
    total_amount: '',
    round_off: '',
    status: 'pending'
  });

  const [invoiceDocument, setInvoiceDocument] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    fetchInitialData();
  }, [id]);

  const fetchInitialData = async () => {
    setIsFetching(true);
    try {
      const [projRes, custRes, taxRes, invRes] = await Promise.all([
        api.get('/projects-dropdown'),
        api.get('/customers-dropdown'),
        api.get('/taxes-dropdown'),
        api.get(`/sales-invoices/${id}`)
      ]);

      if (projRes.data.success) setProjectsData(projRes.data.data);
      if (custRes.data.success) setCustomersData(custRes.data.data);
      if (taxRes.data.success) setTaxesData(taxRes.data.data);

      if (invRes.data.success) {
        const inv = invRes.data.data;
        setFormData({
          invoice_number: inv.invoice_number,
          invoice_date: inv.invoice_date,
          due_date: inv.due_date,
          customer_id: inv.project?.customer_id || '',
          project_id: inv.project_id,
          currency: inv.currency || 'INR',
          notes: inv.remarks || '',
          terms: inv.terms || '',
          total_amount: inv.total_amount,
          round_off: inv.round_off || '',
          status: inv.status
        });

        if (inv.items && inv.items.length > 0) {
          const initialItems = inv.items.map(item => ({
            id: item.work_order_item_id || Math.random().toString(),
            name: item.item_name,
            qty: parseFloat(item.quantity) || 0,
            unit: item.unit?.unit_name || '—',
            unit_id: item.unit_id,
            rate: parseFloat(item.rate) || 0,
            tax_id: item.tax_id,
            tax_percentage: item.tax ? parseFloat(item.tax.tax_percentage) : 0,
            tax_amount: parseFloat(item.tax_amount) || 0,
            amount: parseFloat(item.total_amount) || 0,
            originalId: item.work_order_item_id || item.id
          }));
          setOrderItems(initialItems);
        }
      }
    } catch (error) {
      console.error('Error fetching initial data:', error);
      toast.error('Failed to load invoice details');
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    if (formData.project_id) {
      fetchWorkOrders(formData.project_id);
    } else {
      setWorkOrdersData([]);
      setSelectedWorkOrders([]);
    }
  }, [formData.project_id]);

  const fetchWorkOrders = async (projectId) => {
    setFetchingWorkOrders(true);
    try {
      const dropdownRes = await api.get(`/work-order-items-dropdown?project_id=${projectId}`);
      if (dropdownRes.data.success) {
        setWorkOrdersData(dropdownRes.data.data);
      }
      const detailsRes = await api.get(`/work-order-items?project_id=${projectId}&per_page=1000`);
      if (detailsRes.data.success) {
        setFullItemsData(detailsRes.data.data.data);
      }
    } catch (error) {
      console.error('Error fetching work orders:', error);
    } finally {
      setFetchingWorkOrders(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setInvoiceDocument(file);
      if (file.type.startsWith('image/')) {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreviewUrl(URL.createObjectURL(file));
      } else {
        setPreviewUrl(null);
      }
    }
  };

  const handleRemoveFile = (e) => {
    e.stopPropagation();
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setInvoiceDocument(null);
    setPreviewUrl(null);
  };

  const handleLoadItems = () => {
    if (selectedWorkOrders.length === 0) {
      toast.error('Please select at least one work order');
      return;
    }

    const selectedData = fullItemsData.filter(item => selectedWorkOrders.includes(item.id));
    if (selectedData.length === 0 && selectedWorkOrders.length > 0) {
      toast.error('Could not find detailed information for selected items');
      return;
    }

    const newItems = selectedData.map(wo => {
      const defaultTax = taxesData[0] || null;
      const rate = parseFloat(wo.sales_price) || 0;
      const qty = parseFloat(wo.quantity) || 1;
      const subtotal = rate * qty;
      const taxPercentage = defaultTax ? parseFloat(defaultTax.tax_percentage) : 0;
      const taxAmount = (subtotal * taxPercentage) / 100;

      return {
        id: wo.id,
        name: wo.item_name,
        qty: qty,
        unit: wo.unit?.unit_name || '—',
        unit_id: wo.unit_id,
        rate: rate,
        tax_id: defaultTax ? defaultTax.id : '',
        tax_percentage: taxPercentage,
        tax_amount: taxAmount,
        amount: subtotal + taxAmount,
        originalId: wo.id
      };
    });

    setOrderItems(prev => {
      const existingIds = prev.map(i => i.originalId || i.id);
      const uniqueNewItems = newItems.filter(i => !existingIds.includes(i.id));
      return [...prev, ...uniqueNewItems];
    });

    setSelectedWorkOrders([]);
    toast.success(`${newItems.length} items loaded`);
  };

  const removeOrderItem = (id) => {
    setOrderItems(prev => prev.filter(item => item.id !== id));
  };

  const handleItemChange = (id, field, value) => {
    setOrderItems(prev => prev.map(item => {
      if (item.id === id) {
        const updatedItem = { ...item, [field]: value };
        if (['qty', 'rate', 'tax_id'].includes(field)) {
          const qty = field === 'qty' ? parseFloat(value) || 0 : item.qty;
          const rate = field === 'rate' ? parseFloat(value) || 0 : item.rate;
          const subtotal = qty * rate;

          let taxPercentage = item.tax_percentage;
          if (field === 'tax_id') {
            const selectedTax = taxesData.find(t => t.id === value);
            taxPercentage = selectedTax ? parseFloat(selectedTax.tax_percentage) : 0;
            updatedItem.tax_id = value;
          }

          const taxAmount = (subtotal * taxPercentage) / 100;
          updatedItem.tax_percentage = taxPercentage;
          updatedItem.tax_amount = taxAmount;
          updatedItem.amount = subtotal + taxAmount;
        }
        return updatedItem;
      }
      return item;
    }));
  };

  const calculateTotals = () => {
    const subtotal = orderItems.reduce((acc, item) => acc + (item.qty * item.rate), 0);
    const taxAmount = orderItems.reduce((acc, item) => acc + item.tax_amount, 0);
    const roundOff = parseFloat(formData.round_off) || 0;
    const total = subtotal + taxAmount + roundOff;
    return { subtotal, taxAmount, roundOff, total, taxPercentage: subtotal > 0 ? ((taxAmount / subtotal) * 100).toFixed(2) : 0 };
  };

  const totals = calculateTotals();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (orderItems.length === 0) {
      toast.error('Please add at least one item to the invoice');
      return;
    }

    if (!formData.project_id || !formData.invoice_number || !formData.invoice_date) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      const { roundOff, total } = calculateTotals();

      const payload = {
        project_id: formData.project_id,
        invoice_number: formData.invoice_number,
        invoice_date: formData.invoice_date instanceof Date
          ? formData.invoice_date.toISOString().split('T')[0]
          : formData.invoice_date,
        due_date: formData.due_date instanceof Date
          ? formData.due_date.toISOString().split('T')[0]
          : formData.due_date,
        total_amount: total,
        round_off: roundOff,
        status: formData.status,
        remarks: formData.notes,
        items: orderItems.map(item => ({
          work_order_item_id: item.originalId || item.id,
          item_name: item.name,
          rate: item.rate,
          quantity: item.qty,
          unit_id: item.unit_id,
          subtotal_amount: item.qty * item.rate,
          tax_id: item.tax_id,
          tax_amount: item.tax_amount,
          total_amount: item.amount
        }))
      };

      const response = await api.put(`/sales-invoices/${id}`, payload);

      if (response.data.success) {
        toast.success(response.data.message || 'Sales Invoice updated successfully!');
        navigate('/invoices');
      }
    } catch (error) {
      console.error('Error updating invoice:', error);
      const errorMsg = error.response?.data?.message || 'Failed to update invoice';
      toast.error(errorMsg);

      if (error.response?.data?.errors) {
        const firstError = Object.values(error.response.data.errors)[0][0];
        toast.error(firstError);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isFetching) {
    return (
      <div className={styles['create-invoice-container']}>
        {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
        <div className={styles['content-wrapper']}>
          <Header onMenuClick={() => setIsSidebarOpen(true)} />
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Loader2 className={styles['spinner']} size={40} />
            <span style={{ marginLeft: '12px', fontWeight: 600, color: '#64748b' }}>Technical details loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!formData.invoice_number) {
    return <NotFound />;
  }

  return (
    <div className={styles['create-invoice-container']}>
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={() => setIsSidebarOpen(true)} />

        <main className={styles['main-content']}>
          <Breadcrumbs />

          <div className={styles['page-header-row']}>
            <h1 className={styles['create-invoice-title']}>Edit Sales Invoice</h1>
          </div>

          <form onSubmit={handleSubmit}>
            <div className={styles['form-sections-grid']}>
              <div className={styles['form-card']}>
                <div className={styles['card-header']}>
                  <Info size={18} className={styles['text-orange']} />
                  <h3>Basic Information</h3>
                </div>

                <div className={styles['form-grid-2']}>
                  <div className={styles['form-group']}>
                    <label>Sales Invoice Number</label>
                    <input
                      type="text"
                      name="invoice_number"
                      value={formData.invoice_number}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className={styles['form-group']}>
                    <label>Status</label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      className={styles['select-input']}
                    >
                      <option value="pending">Pending</option>
                      <option value="partial_paid">Partial Paid</option>
                      <option value="paid">Paid</option>
                      <option value="overdue">Overdue</option>
                    </select>
                  </div>
                </div>

                <div className={styles['form-grid-2']}>
                  <div className={styles['form-group']}>
                    <label>Sales Invoice Date</label>
                    <CustomDatePicker
                      selected={formData.invoice_date}
                      onChange={(date) => setFormData(prev => ({ ...prev, invoice_date: date }))}
                    />
                  </div>
                  <div className={styles['form-group']}>
                    <label>Project</label>
                    <CustomSelect
                      placeholder="Select Project"
                      value={formData.project_id ? {
                        value: formData.project_id,
                        label: projectsData.find(p => p.id === formData.project_id)?.project_name
                      } : null}
                      options={projectsData.map(p => ({ value: p.id, label: p.project_name }))}
                      onChange={(opt) => {
                        const projId = opt ? opt.value : '';
                        const project = projectsData.find(p => p.id === projId);
                        setFormData(prev => ({
                          ...prev,
                          project_id: projId,
                          customer_id: project ? project.customer_id : prev.customer_id
                        }));
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className={styles['form-card']}>
                <div className={styles['card-header']}>
                  <FileText size={18} className={styles['text-orange']} />
                  <h3>Attachments & Notes</h3>
                </div>

                <div className={styles['dropzone']} onClick={() => !invoiceDocument && document.getElementById('file-upload').click()}>
                  <input
                    id="file-upload"
                    type="file"
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                    accept=".pdf,.jpg,.png,.jpeg"
                  />
                  {previewUrl ? (
                    <div className={styles['preview-container']}>
                      <img src={previewUrl} alt="Preview" className={styles['preview-image']} />
                      <button type="button" className={styles['remove-preview']} onClick={handleRemoveFile}>
                        <X size={14} />
                      </button>
                    </div>
                  ) : invoiceDocument ? (
                    <div className={styles['file-selected-info']}>
                      <FileText size={24} className={styles['text-orange']} />
                      <div className={styles['file-details']}>
                        <p className={styles['file-name']}>{invoiceDocument.name}</p>
                        <p className={styles['file-meta']}>File selected</p>
                      </div>
                      <button type="button" className={styles['remove-preview-btn']} onClick={handleRemoveFile}>
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <>
                      <Upload size={32} className={styles['upload-icon']} />
                      <p>Drag & drop or <span className={styles['text-orange']}>browse</span></p>
                      <span className={styles['upload-hint']}>PDF, JPG up to 10MB</span>
                    </>
                  )}
                </div>

                <div className={styles['form-group']} style={{ marginTop: '20px' }}>
                  <label>Additional Notes</label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    placeholder="Enter any notes for the client..."
                  />
                </div>
              </div>
            </div>

            <div className={styles['form-card']} style={{ marginTop: '20px' }}>
              <div className={styles['card-header']}>
                <LinkIcon size={18} className={styles['text-orange']} />
                <h3>Link Work Order Items</h3>
              </div>
              <div className={styles['card-content']}>
                <div style={{ display: 'flex', gap: '15px', alignItems: 'flex-end' }}>
                  <div className={styles['form-group']} style={{ flex: 1, marginBottom: 0 }}>
                    <label>Select Work Orders</label>
                    <CustomSelect
                      isMulti={true}
                      options={workOrdersData.map(wo => ({ value: wo.id, label: `${wo.item_name} (#ITEM-${wo.id})` }))}
                      value={workOrdersData
                        .filter(wo => selectedWorkOrders.includes(wo.id))
                        .map(wo => ({ value: wo.id, label: `${wo.item_name} (#ITEM-${wo.id})` }))}
                      onChange={(selected) => {
                        setSelectedWorkOrders(selected ? selected.map(s => s.value) : []);
                      }}
                      placeholder="Search and select work order items..."
                      isClearable={true}
                      isLoading={fetchingWorkOrders}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleLoadItems}
                    title="Load Selected Items"
                    style={{
                      backgroundColor: '#27B872',
                      color: 'white',
                      border: 'none',
                      padding: '10px 15px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      height: '42px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Check size={20} />
                  </button>
                </div>
              </div>
            </div>

            <div className={styles['form-card']} style={{ marginTop: '20px' }}>
              <div className={styles['card-header']}>
                <List size={18} className={styles['text-orange']} />
                <h3>Invoice Items</h3>
              </div>
              <div className={styles['card-content']}>
                {orderItems.length > 0 ? (
                  <div style={{ overflowX: 'auto', marginTop: '15px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #eee' }}>
                          <th style={{ padding: '12px', color: '#64748b' }}>Item Code</th>
                          <th style={{ padding: '12px', color: '#64748b' }}>Item Name</th>
                          <th style={{ padding: '12px', color: '#64748b' }}>Quantity</th>
                          <th style={{ padding: '12px', color: '#64748b' }}>Unit</th>
                          <th style={{ padding: '12px', color: '#64748b' }}>Rate</th>
                          <th style={{ padding: '12px', color: '#64748b' }}>Tax</th>
                          <th style={{ padding: '12px', color: '#64748b' }}>Amount</th>
                          <th style={{ padding: '12px' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {orderItems.map(item => (
                          <tr key={item.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ padding: '12px', fontWeight: '500' }}>#ITEM-{item.originalId || item.id}</td>
                            <td style={{ padding: '12px' }}>{item.name}</td>
                            <td style={{ padding: '12px' }}>
                              <input
                                type="text"
                                style={{ width: '80px', padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: '6px' }}
                                value={item.qty}
                                onChange={(e) => handleItemChange(item.id, 'qty', e.target.value)}
                              />
                            </td>
                            <td style={{ padding: '12px', color: '#64748b' }}>{item.unit}</td>
                            <td style={{ padding: '12px' }}>
                              <div style={{ display: 'flex', alignItems: 'center' }}>
                                <span style={{ color: '#64748b', marginRight: '6px' }}>₹</span>
                                <input
                                  type="text"
                                  style={{ width: '100px', padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: '6px' }}
                                  value={item.rate}
                                  onChange={(e) => handleItemChange(item.id, 'rate', e.target.value)}
                                />
                              </div>
                            </td>
                            <td style={{ padding: '12px', minWidth: '150px' }}>
                              <CustomSelect
                                value={item.tax_id ? {
                                  value: item.tax_id,
                                  label: taxesData.find(t => t.id === item.tax_id)?.tax_name
                                } : null}
                                onChange={(opt) => handleItemChange(item.id, 'tax_id', opt ? opt.value : '')}
                                options={taxesData.map(t => ({ value: t.id, label: `${t.tax_name} (${t.tax_percentage}%)` }))}
                                placeholder="Select Tax"
                                isClearable={true}
                              />
                            </td>
                            <td style={{ padding: '12px', fontWeight: '600' }}>₹{item.amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                            <td style={{ padding: '12px', textAlign: 'right' }}>
                              <button
                                type="button"
                                onClick={() => removeOrderItem(item.id)}
                                style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '4px' }}
                              >
                                <Trash2 size={18} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px', padding: '20px', backgroundColor: '#f8fafc', borderRadius: '8px' }}>
                      <div style={{ width: '300px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', color: '#64748b' }}>
                          <span>Subtotal:</span>
                          <span style={{ fontWeight: '500', color: '#334155' }}>₹{totals.subtotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', color: '#64748b' }}>
                          <span>Tax ({totals.taxPercentage}%):</span>
                          <span style={{ fontWeight: '500', color: '#334155' }}>+ ₹{totals.taxAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', alignItems: 'center', color: '#64748b' }}>
                          <span>Round Off:</span>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <span style={{ marginRight: '6px' }}>₹</span>
                            <input
                              type="number"
                              style={{ width: '100px', textAlign: 'right', padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: '6px' }}
                              value={formData.round_off}
                              onChange={(e) => setFormData(prev => ({ ...prev, round_off: e.target.value }))}
                              placeholder="0.00"
                              step="0.01"
                            />
                          </div>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0 0 0', marginTop: '8px', borderTop: '2px solid #e2e8f0', fontSize: '18px', fontWeight: '700', color: '#0f172a' }}>
                          <span>Total Amount:</span>
                          <span>₹{totals.total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px 20px', color: '#94a3b8' }}>
                    <p>No items added yet. Link a work order item above.</p>
                  </div>
                )}
              </div>
            </div>

            <div className={styles['footer-actions']}>
              <button type="button" className={styles['btn-secondary']} onClick={() => navigate('/invoices')}>Cancel</button>
              <button type="submit" className={styles['btn-primary']} disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 size={18} className={styles['spinner']} />
                    <span>Updating...</span>
                  </>
                ) : (
                  <span>Update Sales Invoice</span>
                )}
              </button>
            </div>
          </form>
        </main>
      </div>
    </div>
  );
};

export default EditInvoice;
